/** Centralized color palette, typography constants, text styles, and button utilities. */
import Phaser from 'phaser';
import type { LayoutTier } from './TreeLayoutEngine';

// ---------------------------------------------------------------------------
// Color Palette
// ---------------------------------------------------------------------------

/** Convert 0xRRGGBB to '#rrggbb' */
function hex(n: number): string {
  return '#' + n.toString(16).padStart(6, '0');
}

/** Numeric colors for Graphics, Rectangles, and Circles. */
export const Color = {
  // Greens
  greenBright:    0x00ff00,
  greenDefault:   0x00cc00,

  // Oranges / Warning
  orangeBright:   0xff8800,
  orangeDefault:  0xcc6600,

  // Reds / Danger
  redBright:      0xff0000,
  redDefault:     0xcc0000,

  // Blues / Attunement
  blueBright:     0x66aaff,
  blueDefault:    0x4488cc,
  cyan:           0x44ddff,

  // Debug orange
  debugBright:    0xff8800,
  debugDefault:   0xff6600,

  // Text grays
  textPrimary:    0xcccccc,
  textSecondary:  0x888888,
  textMuted:      0x666666,
  textDisabled:   0x444444,
  textDim:        0x333333,

  // Surfaces
  sceneBg:        0x111111,
  panelBg:        0x222222,
  overlayBg:      0x333333,
  black:          0x000000,

  // HUD — CombatHUD
  hpFill:         0xcc2222,
  hpBorder:       0x555555,
  staminaFill:    0xddcc00,
  staminaEmpty:   0x886600,
  dashReady:      0x22cc44,
  dashUnavail:    0xcc2222,

  // HUD — AttunementPanel
  attuneFilled:   0x9900ff,
  attuneBorder:   0x888888,

  // Tree Visualization — nodes
  treeNodeLocked:     0x333333,
  treeNodeAvailable:  0xffffff,
  treeNodeChosen:     0x66aa66,
  treeNodeEquipped:   0x44ddff,
  treeNodePassive:    0x66cc66,

  // Tree Visualization — branches
  treeBranch:         0x333333,
  treeBranchGrown:    0x88aa88,
  treeBranchGrowing:  0x556655,

  // Tree Visualization — tiers
  treeTierFoundation:      0x66aa66,
  treeTierThreshold:   0x4488cc,
  treeTierTranscendent:0xcc8844,
  treeTierApotheosis:      0xcc44cc,

  // Tree Visualization — combos
  treeComboLink:      0xaa66ff,
} as const;

/** Hex string colors for Text objects — auto-derived from Color, same keys. */
export const Hex = Object.fromEntries(
  Object.entries(Color).map(([k, v]) => [k, hex(v)])
) as { readonly [K in keyof typeof Color]: string };

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

// Available fonts (swap the first entry to switch):
//   'Vipnagorgialla'   — sci-fi geometric, clean
//   'No Continue'       — pixel/retro
//   'Toxigenesis'       — heavy/industrial
//   'Wheaton Capitals'  — decorative capitals
//   'monospace'         — browser default fallback
export const Font = {
  family: 'Vipnagorgialla, monospace',
  // family: 'No Continue, monospace',
  // family: 'Toxigenesis, monospace',
  // family: 'Wheaton Capitals, monospace',

  sizeGameTitle: '48px',
  sizeTitleLg:   '36px',
  sizeTitleMd:   '32px',
  sizeTitle:     '28px',
  sizeButton:    '24px',
  sizeButtonSm:  '20px',
  sizeButtonXs:  '18px',
  sizeBody:      '14px',
  sizeMaster:    '15px',
  sizeSmall:     '13px',
  sizeTiny:      '12px',
  sizeCaption:   '11px',
  sizeLabel:     '10px',
  sizeXs:        '9px',
  sizeCrawl:     '7px',
  sizeWorld:     '8px',
  sizeDeathCounter: '120px',
} as const;

// ---------------------------------------------------------------------------
// Pre-composed TextStyles
// ---------------------------------------------------------------------------

/** Zero-offset black glow for world-space text readability over any background. */
export const worldShadow: Phaser.Types.GameObjects.Text.TextShadow = {
  offsetX: 0, offsetY: 0, color: Hex.black, blur: 12, stroke: true, fill: true,
};

export const TextStyle = {
  debugTitle: { fontFamily: Font.family, fontSize: Font.sizeTitle, color: Hex.debugDefault } as Phaser.Types.GameObjects.Text.TextStyle,
  body:       { fontFamily: Font.family, fontSize: Font.sizeBody, color: Hex.textPrimary } as Phaser.Types.GameObjects.Text.TextStyle,
  bodyMuted:  { fontFamily: Font.family, fontSize: Font.sizeBody, color: Hex.textSecondary } as Phaser.Types.GameObjects.Text.TextStyle,
  bodyKey:    { fontFamily: Font.family, fontSize: Font.sizeBody, color: Hex.greenDefault } as Phaser.Types.GameObjects.Text.TextStyle,
  section:    { fontFamily: Font.family, fontSize: Font.sizeTiny, color: Hex.textSecondary } as Phaser.Types.GameObjects.Text.TextStyle,
  small:      { fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.textPrimary } as Phaser.Types.GameObjects.Text.TextStyle,
  smallMuted: { fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.textSecondary } as Phaser.Types.GameObjects.Text.TextStyle,
  worldLabel: { fontFamily: Font.family, fontSize: Font.sizeWorld, color: Hex.textSecondary, stroke: Hex.black, strokeThickness: 3, shadow: worldShadow } as Phaser.Types.GameObjects.Text.TextStyle,

  // Debug overlay styles — world-space labels above entities (need stroke for variable backgrounds)
  debugWorld: { fontFamily: Font.family, fontSize: Font.sizeWorld, stroke: Hex.black, strokeThickness: 3, shadow: worldShadow } as Phaser.Types.GameObjects.Text.TextStyle,
  // Debug overlay styles — HUD-fixed panel text (no stroke; sits on dark bg rect)
  debugPanel: { fontFamily: Font.family, fontSize: Font.sizeXs, color: Hex.textPrimary } as Phaser.Types.GameObjects.Text.TextStyle,
  // Event crawl panel (HUD component — smallest readable size for dense log)
  crawlPanel: { fontFamily: Font.family, fontSize: Font.sizeCrawl, color: Hex.textPrimary } as Phaser.Types.GameObjects.Text.TextStyle,
  // Debug overlay styles — panel section titles
  debugPanelTitle: { fontFamily: Font.family, fontSize: Font.sizeCaption, color: Hex.textMuted } as Phaser.Types.GameObjects.Text.TextStyle,
} as const;

/** Standard alpha for world-space debug overlays (labels above enemies). */
export const DEBUG_WORLD_ALPHA = 0.85;
/** Standard alpha for debug panel text (crawl overlay, danger panel). */
export const DEBUG_PANEL_BG_ALPHA = 0.4;

// ---------------------------------------------------------------------------
// UI Opacity / Alpha
// ---------------------------------------------------------------------------

/** Alpha for tier labels drawn on the left edge of the tree detail view. */
export const NODE_TIER_LABEL_ALPHA = 0.8;

/** Alpha for the fork-available pulse ring drawn beneath choosable fork nodes. */
export const NODE_FORK_PULSE_ALPHA = 0.5;

/** Alpha for the "hover a node for details" hint text in the info panel. */
export const NODE_INFO_HINT_ALPHA = 0.6;

/** Alpha for the node type / status line in the hover info panel. */
export const NODE_INFO_TYPE_ALPHA = 0.7;

/** Alpha for the flavor text line in the hover info panel. */
export const NODE_INFO_FLAVOR_ALPHA = 0.8;

/** Alpha for the description text line in the hover info panel. */
export const NODE_INFO_DESC_ALPHA = 0.7;

/** Alpha for the XP-bar background track in the tree cluster overview. */
export const CLUSTER_BAR_BG_ALPHA = 0.6;

/** Alpha for the XP-bar fill in the tree cluster overview. */
export const CLUSTER_BAR_FILL_ALPHA = 0.8;

/** Alpha for the selection highlight border on a tree cluster card. */
export const CLUSTER_SELECTION_ALPHA = 0.6;

/** Alpha for the background rect of the slot panel chrome. */
export const SLOT_PANEL_BG_ALPHA = 0.4;

// ---------------------------------------------------------------------------
// Button Colors
// ---------------------------------------------------------------------------

export const ButtonColor = {
  primary:  { default: Hex.greenDefault,  hover: Hex.greenBright },
  warning:  { default: Hex.orangeDefault, hover: Hex.orangeBright },
  danger:   { default: Hex.redDefault,    hover: Hex.redBright },
  debug:    { default: Hex.debugDefault,  hover: Hex.debugBright },
  attune:   { default: Hex.blueDefault,   hover: Hex.blueBright },
  muted:    { default: Hex.textSecondary, hover: Hex.textPrimary },
  spawn:    { default: '#33cc33', hover: '#44ff44' },
  despawn:  { default: '#cc3333', hover: '#ff4444' },
} as const;

// ---------------------------------------------------------------------------
// Tree Visualization — Node Geometry
// ---------------------------------------------------------------------------

export const ACTIVE_RADIUS = 10;
export const PASSIVE_RADIUS = 5;
export const UPGRADE_RADIUS = 2;
export const EVOLUTION_RADIUS = 11;

/** Return the display radius for a V4 node type. */
export function getNodeRadius(nodeType: string): number {
  switch (nodeType) {
    case 'passive': return PASSIVE_RADIUS;
    case 'upgrade': return UPGRADE_RADIUS;
    case 'evolution': return EVOLUTION_RADIUS;
    default: return ACTIVE_RADIUS;
  }
}

/** Return the tier color for a layout tier. */
export function getTierColor(tier: LayoutTier): number {
  switch (tier) {
    case 'foundation': return Color.treeTierFoundation;
    case 'threshold': return Color.treeTierThreshold;
    case 'transcendent': return Color.treeTierTranscendent;
    case 'apotheosis': return Color.treeTierApotheosis;
  }
}

// ---------------------------------------------------------------------------
// Button Colors
// ---------------------------------------------------------------------------

/** Wire standard hover color behavior on an interactive text button. */
export function applyButtonHover(
  text: Phaser.GameObjects.Text,
  colors: { default: string; hover: string },
): Phaser.GameObjects.Text {
  text.on('pointerover', () => text.setColor(colors.hover));
  text.on('pointerout', () => text.setColor(colors.default));
  return text;
}
